/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/require-default-props */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/**
 * @deprecated This component is deprecated and will be removed in a future release.
 * Please use the new Quimby UI inline input components instead:
 *
 * For text editing: `import { InlineText } from '@ui/molecules/inputs';`
 * For dropdowns: `import { InlineSelect } from '@ui/molecules/inputs';`
 * For dates: `import { InlineDate } from '@ui/molecules/inputs';`
 *
 * Or from inline subdirectory: `import { InlineText, InlineSelect, InlineDate } from '@ui/molecules/inputs/inline';`
 *
 * Or specific path: `import { InlineEdit } from '@ui/molecules/inputs/InlineEdit';`
 *
 * See the new component for documentation and migration notes.
 */
import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import { TimePicker } from '@progress/kendo-react-dateinputs';
import { InlineEditDate } from './InlineEditDate';
import { InlineEditPhone } from './InlineEditPhone';
import { InlineEditNumber } from './InlineEditNumber';
export function InlineEdit(_a) {
    var { as, children, className, variant, disableEdit = false, placeholder = '--', label, spellCheck = true, id, 'aria-labelledby': ariaLabelledby, dateValue, onDateChange, onTimeChange, phoneValue, onPhoneChange, onEditStart, onEditEnd, numberValue, onNumberChange, allowDecimals, allowNegative, maxLength, onClick, refComp } = _a, props = __rest(_a, ["as", "children", "className", "variant", "disableEdit", "placeholder", "label", "spellCheck", "id", 'aria-labelledby', "dateValue", "onDateChange", "onTimeChange", "phoneValue", "onPhoneChange", "onEditStart", "onEditEnd", "numberValue", "onNumberChange", "allowDecimals", "allowNegative", "maxLength", "onClick", "refComp"]);
    const Component = as || 'div';
    const variantClass = variant ? `qmb-inedit--${variant}` : 'qmb-inedit';
    const [isOpen, setIsOpen] = useState(false);
    const handleKeyDown = (e) => {
        var _a, _b;
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            (_a = e.currentTarget) === null || _a === void 0 ? void 0 : _a.blur();
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            (_b = e.currentTarget) === null || _b === void 0 ? void 0 : _b.blur();
        }
    };
    const CustomInput = useCallback((inputProps) => {
        const formattedDate = inputProps.value instanceof Date && !Number.isNaN(inputProps.value.getTime())
            ? inputProps.value
                .toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            })
                .toLowerCase()
            : children || placeholder;
        return (React.createElement(Component, Object.assign({ className: classNames(variantClass, className), onClick: () => {
                if (!disableEdit) {
                    setIsOpen((prev) => !prev);
                }
            }, "data-placeholder": placeholder, "data-label": label, id: id, disabled: disableEdit, "aria-labelledby": ariaLabelledby }, props), formattedDate));
    }, [children, placeholder, variantClass, className, disableEdit, setIsOpen, props, id, ariaLabelledby]);
    // Handle rendering a phone inline edit when variant is 'phone'
    if (variant === 'phone') {
        return (React.createElement(InlineEditPhone, Object.assign({ as: Component, className: className, disableEdit: disableEdit, placeholder: placeholder, label: label, id: id, "aria-labelledby": ariaLabelledby, spellCheck: spellCheck, phoneValue: phoneValue, onPhoneChange: onPhoneChange, onEditStart: onEditStart, onEditEnd: onEditEnd }, props), children));
    }
    // Handle rendering a number inline edit when variant is 'number'
    if (variant === 'number') {
        return (React.createElement(InlineEditNumber, Object.assign({ as: Component, className: className, disableEdit: disableEdit, placeholder: placeholder, label: label, id: id, "aria-labelledby": ariaLabelledby, numberValue: numberValue, onNumberChange: onNumberChange, allowDecimals: allowDecimals, allowNegative: allowNegative, maxLength: maxLength }, props), children));
    }
    // Handle rendering a date picker when variant is 'date'
    if (variant === 'date') {
        return (React.createElement(InlineEditDate, Object.assign({ as: Component, className: className, disableEdit: disableEdit, placeholder: placeholder, label: label, id: id, "aria-labelledby": ariaLabelledby, dateValue: dateValue, onDateChange: onDateChange }, props), children));
    }
    // Handle rendering a time picker when variant is 'time'
    if (variant === 'time') {
        const handleTimeChange = (e) => {
            setIsOpen(false);
            if (onTimeChange && e.value instanceof Date) {
                onTimeChange(e.value);
            }
        };
        return (React.createElement("div", null,
            React.createElement(TimePicker, { value: dateValue instanceof Date && !Number.isNaN(dateValue.getTime()) ? dateValue : null, onChange: handleTimeChange, show: isOpen, onClose: () => setIsOpen(false), onOpen: () => setIsOpen(true), dateInput: CustomInput })));
    }
    // variant is 'dropdown'
    if (variant === 'dropdown') {
        const handleClick = () => {
            if (!disableEdit && onClick) {
                onClick();
            }
        };
        return (React.createElement("div", null,
            React.createElement(Component, Object.assign({ id: id, ref: refComp, className: classNames({ [variantClass]: !disableEdit }, className), onClick: handleClick }, props), children)));
    }
    // Default inline edit component
    return (React.createElement(Component, Object.assign({ className: classNames({ [variantClass]: !disableEdit }, className), contentEditable: !disableEdit, suppressContentEditableWarning: !disableEdit, "data-placeholder": placeholder, "data-label": label, onKeyDown: !disableEdit ? handleKeyDown : undefined, spellCheck: !disableEdit ? spellCheck : false, id: id, "aria-labelledby": ariaLabelledby }, props), children));
}
